import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@bliks-io/ui';
import type { PopulatedEntitySuggestion } from '@bliks-io/contracts';
import { useEntitySuggestionsForApprover } from '@/api/queries/useEntitySuggestionsForApprover';
import { usePendingReviewsColumns } from '../hooks/usePendingReviewsColumns';
import { EntitySuggestionReviewModal } from './EntitySuggestionReviewModal';
import { SourceRecordingPreviewDialog } from './SourceRecordingPreviewDialog';
import { SuggestionProcessPreviewDialog } from './SuggestionProcessPreviewDialog';

type ProcessPreviewSource = Pick<
  PopulatedEntitySuggestion,
  'sourceProcessId' | 'sourceRecordingId'
>;

export function PendingReviewsSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useEntitySuggestionsForApprover();
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);
  const [previewRecordingId, setPreviewRecordingId] = useState<string | null>(null);
  const [previewProcessSource, setPreviewProcessSource] = useState<ProcessPreviewSource | null>(
    null
  );

  const columns = usePendingReviewsColumns({
    onReviewClick: setSelectedSuggestionId,
    onPreviewRecording: setPreviewRecordingId,
    onPreviewProcess: setPreviewProcessSource
  });

  const suggestions = data ?? [];
  const hasResults = suggestions.length > 0;

  return (
    <>
      {(isLoading || hasResults) && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold leading-6">{t('myTasks.pendingReviewsTitle')}</h2>
            <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
              {t('myTasks.pendingReviewsDescription')}
            </p>
          </div>
          <DataTable
            columns={columns}
            data={suggestions}
            isLoading={isLoading}
            emptyMessage={t('myTasks.noPendingReviews')}
            enableGlobalFilter
            globalSearchPlaceholder={t('myTasks.searchPendingReviews')}
            enableColumnVisibility
            enableColumnFilters
            enableUrlState
            tableId="pendingReviews"
            initialSorting={[{ id: 'submittedAt', desc: true }]}
          />
        </div>
      )}
      <EntitySuggestionReviewModal
        suggestionId={selectedSuggestionId}
        listSuggestion={suggestions.find((s) => s._id === selectedSuggestionId)}
        onClose={() => setSelectedSuggestionId(null)}
        onPreviewRecording={setPreviewRecordingId}
      />
      <SourceRecordingPreviewDialog
        recordingId={previewRecordingId}
        onClose={() => setPreviewRecordingId(null)}
      />
      {previewProcessSource && (
        <SuggestionProcessPreviewDialog
          suggestion={previewProcessSource}
          open
          onClose={() => setPreviewProcessSource(null)}
        />
      )}
    </>
  );
}
